import { FESTIVAL, OPPOSITE, axisOf, entrySpot, exitSpot, pathId } from '../../data/full/festival';
import type { EdgeSide } from '../../data/types';
import { rotate } from '../../shared/angle';
import { clamp, clamp01 } from '../../shared/scalar';
import { isBarTick } from '../../shared/tempo';
import { boardSpots, pathWithObstacles } from '../../data/full/path-layout';
import { unitFromAngle } from '../../shared/angle';
import { lookup } from '../lookup';
import { stageEarned, stageGains } from '../score';
import { enterSet } from '../enter-set';
import { clearOfObstacles } from '../obstacles';
import type { GameMode } from '../mode';
import type {
  FestivalChallenge,
  FestivalExit,
  FestivalState,
  PlayerId,
  PlayerState,
  SimEvent,
  SimState,
  Vec2,
} from '../state';
import { applyDerivedStats } from '../stats';
import { isSetFinished } from '../systems/set-progress';
import type { StepContext } from '../systems/types';
import { festivalBotMove } from './festival-bot';
import { challengeScoreMul, stageScale } from './festival-scale';

// A vibe is drawn about that wide.
const VIBE_GAP = 8;

export const festival: GameMode = {
  start({ state }) {
    if (!FESTIVAL.setIds.includes(state.setId)) {
      throw new Error(`the festival does not play the set "${state.setId}"`);
    }
    if (state.set.endlessFromTick !== undefined) {
      throw new Error('a festival does not go on endlessly');
    }
    state.festival = {
      phase: 'scene',
      scenes: [{ setId: state.setId, fromTick: state.tick }],
      exits: [],
      standing: state.players.map(() => null),
      departTicks: 0,
    };
  },
  systems: [festivalStep],
  finishWins: ({ state }) => stagesLeft(festivalOf(state)).length === 0,
  botMove: festivalBotMove,
};

function festivalOf(state: SimState): FestivalState {
  if (state.festival === undefined) {
    throw new Error('a festival game has no festival state');
  }
  return state.festival;
}

function stagesLeft(festival: FestivalState): string[] {
  return FESTIVAL.setIds.filter((id) => !festival.scenes.some((scene) => scene.setId === id));
}

function festivalStep(ctx: StepContext): void {
  const festival = festivalOf(ctx.state);
  switch (festival.phase) {
    case 'scene':
      if (isSetFinished(ctx.state.set, ctx.set)) {
        holdStage(ctx, festival);
      }
      return;
    case 'sunrise':
      gather(ctx, festival);
      return;
    case 'path':
      walk(ctx, festival);
      return;
  }
}

function holdStage(ctx: StepContext, festival: FestivalState): void {
  const { state, content } = ctx;
  const scene = festival.scenes.at(-1);
  if (scene !== undefined) {
    scene.toTick = state.tick;
    if (scene.challenges !== undefined) {
      scene.challenges.earned = stageEarned(state, scene.challenges.scoreFrom);
    }
  }
  state.events.push({ type: 'festivalSceneHeld', setId: state.setId });
  const left = stagesLeft(festival);
  if (left.length === 0) {
    return;
  }
  for (const enemy of state.enemies) {
    enemy.escaped = true;
    enemy.hp = 0;
  }
  state.projectiles = state.projectiles.filter((shot) => shot.owner.kind !== 'enemy');
  burstTraps(ctx);
  for (const player of state.players) {
    if (player.downed) {
      player.downed = false;
      player.reviveTicks = 0;
      state.events.push({ type: 'playerRevived', playerId: player.id });
    }
    if ((player.suppressedTicks ?? 0) > 0) {
      player.suppressedTicks = 0;
      applyDerivedStats(player, lookup(content.classes, player.classId, 'class'));
    }
    if (player.hp < player.maxHp) {
      state.events.push({
        type: 'playerHealed',
        playerId: player.id,
        amount: player.maxHp - player.hp,
      });
      player.hp = player.maxHp;
    }
  }
  const places = FESTIVAL.exits[state.setId] ?? [];
  festival.exits = left.map((setId, index): FestivalExit => {
    const place = places[index];
    if (place === undefined) {
      throw new Error(`the set "${state.setId}" has no exit toward "${setId}"`);
    }
    return { setId, side: place.side, ...exitSpot(state.arena, place) };
  });
  festival.phase = 'sunrise';
}

// So the team sees its exits: each trap left placed leaves the stage as vibes thrown around it.
function burstTraps({ state, set, tempo, obstacles }: StepContext): void {
  const { drops, amount, radius, flightBeats } = FESTIVAL.trapVibes;
  const ticks = flightBeats * tempo.ticksPerBeat;
  for (const { id, kind, ownerId, x, y } of state.traps) {
    state.events.push({ type: 'trapBurst', id, kind, playerId: ownerId, x, y });
    for (let drop = 0; drop < drops; drop++) {
      // Turned by the trap's id, so that two traps side by side do not throw on the same lines.
      const side = rotate({ x: radius, y: 0 }, id + (drop / drops) * 2 * Math.PI);
      const landing = clearOfObstacles({ x: x + side.x, y: y + side.y }, VIBE_GAP, obstacles);
      state.pickups.push({
        id: state.nextEntityId,
        kind: 'vibes',
        amount,
        x: landing.x,
        y: landing.y,
        prevX: landing.x,
        prevY: landing.y,
        ticksLeft: set.pickups.lifetimeTicks,
        flight: { ticksLeft: ticks, ticks, fromX: x, fromY: y },
      });
      state.nextEntityId += 1;
    }
  }
  state.traps = [];
}

function gather(ctx: StepContext, festival: FestivalState): void {
  const { state, tempo } = ctx;
  festival.standing = state.players.map((player) => {
    const index = festival.exits.findIndex((exit) => {
      const dx = player.x - exit.x;
      const dy = player.y - exit.y;
      return dx * dx + dy * dy <= exit.radius * exit.radius;
    });
    return index === -1 ? null : index;
  });
  // A player whose peer left online has no say: the team goes on without them.
  const present = festival.standing.filter((_, slot) => state.players[slot]?.gone !== true);
  const [first] = present;
  const together =
    first !== null && first !== undefined && present.every((index) => index === first);
  festival.departTicks = together ? festival.departTicks + 1 : 0;
  const exit = together ? festival.exits[first] : undefined;
  if (exit !== undefined && festival.departTicks >= FESTIVAL.departBars * tempo.ticksPerBar) {
    leave(ctx, festival, exit);
  }
}

function leave(ctx: StepContext, festival: FestivalState, exit: FestivalExit): void {
  const { state, content } = ctx;
  state.events.push({ type: 'festivalLeft', setId: exit.setId, side: exit.side });
  const path = pathWithObstacles(
    lookup(content.sets, pathId(exit.setId, axisOf(exit.side)), 'set'),
    festival.scenes.at(-1)?.setId,
    exit.setId,
    exit.side,
    state.seed,
    festival.scenes.length,
  );
  const forward = FORWARD[exit.side];
  const start = startOf(path.arena, forward);
  const spots = state.players.map((player) =>
    across(start, forward, offsetIn(player, exit, forward), path.arena, player.radius),
  );
  festival.taken = exit;
  festival.exits = [];
  festival.standing = state.players.map(() => null);
  festival.departTicks = 0;
  festival.phase = 'path';
  enterSet(ctx, path, spots);
  festival.challenges = layBoards(state, path.arena);
}

function layBoards(state: SimState, arena: { width: number; height: number }): FestivalChallenge[] {
  const spots = boardSpots(arena);
  return FESTIVAL.challenges.list.map(({ id }, index) => {
    const spot = spots[index];
    if (spot === undefined) {
      throw new Error(`the path has no board for the challenge "${id}"`);
    }
    return {
      id,
      ...spot,
      radius: FESTIVAL.challenges.radius,
      lit: state.players.map(() => false),
      armed: state.players.map(() => true),
      switched: state.players.map(() => false),
    };
  });
}

// On a bar of the stage ahead's tempo, so that its music goes on in time.
function walk(ctx: StepContext, festival: FestivalState): void {
  const { state } = ctx;
  const exit = takenExit(festival);
  takeChallenges(ctx, festival.challenges ?? []);
  if (
    isBarTick(state.tick, ctx.tempo) &&
    state.players.every(
      (player) =>
        player.gone === true || toEnd(player, state.arena, exit.side) <= FESTIVAL.path.endDepth,
    )
  ) {
    arrive(ctx, festival, exit);
  }
}

// Each player switches their own pastille with a shot toward a board within reach, once they have
// let go of the fire since the last switch: a held fire or a spread of shots switches it once. Once
// every player still there has theirs lit, the board is taken for the team; one put out drops it.
function takeChallenges({ state, commands }: StepContext, boards: FestivalChallenge[]): void {
  state.players.forEach((player, slot) => {
    if (commands.get(player.id)?.input.fire !== true) {
      for (const board of boards) {
        board.armed[slot] = true;
      }
    }
  });
  const shots = state.events.filter(
    (event): event is Extract<SimEvent, { type: 'playerFired' }> => event.type === 'playerFired',
  );
  for (const shot of shots) {
    const slot = state.players.findIndex((player) => player.id === shot.playerId);
    const player = state.players[slot];
    if (player === undefined) {
      continue;
    }
    boards.forEach((board, index) => {
      if (
        board.armed[slot] === true &&
        aimsAt(shot, unitFromAngle(shot.angle), board.radius, board)
      ) {
        board.armed[slot] = false;
        board.switched[slot] = true;
        switchPastille(state, board, index, slot, player.id);
      }
    });
  }
  boards.forEach((board, index) => {
    const present = state.players.flatMap((player, slot) => (player.gone === true ? [] : [slot]));
    if (
      board.takenTick === undefined &&
      present.length > 0 &&
      present.every((slot) => board.lit[slot] === true)
    ) {
      board.takenTick = state.tick;
      delete board.droppedTick;
      state.events.push({ type: 'festivalChallengeTaken', index });
    }
  });
}

function switchPastille(
  state: SimState,
  board: FestivalChallenge,
  index: number,
  slot: number,
  playerId: PlayerId,
): void {
  if (board.lit[slot] !== true) {
    board.lit[slot] = true;
    state.events.push({ type: 'festivalChallengeLit', index, playerId });
    return;
  }
  board.lit[slot] = false;
  state.events.push({ type: 'festivalChallengeUnlit', index, playerId });
  if (board.takenTick !== undefined) {
    delete board.takenTick;
    board.droppedTick = state.tick;
    state.events.push({ type: 'festivalChallengeDropped', index });
  }
}

// Whether a line from `from` along `way` passes within `radius` of the board, no farther than reach.
function aimsAt(from: Vec2, way: Vec2, radius: number, board: FestivalChallenge): boolean {
  const { reach } = FESTIVAL.challenges;
  const dx = board.x - from.x;
  const dy = board.y - from.y;
  const along = dx * way.x + dy * way.y;
  const off = dx * way.y - dy * way.x;
  return (
    (way.x !== 0 || way.y !== 0) &&
    along >= 0 &&
    along <= reach + radius &&
    off * off <= radius * radius
  );
}

function takenExit(festival: FestivalState): FestivalExit {
  if (festival.taken === undefined) {
    throw new Error('a festival walks a path without an exit taken');
  }
  return festival.taken;
}

export function toEnd(
  point: Vec2,
  arena: { width: number; height: number },
  side: EdgeSide,
): number {
  switch (side) {
    case 'right':
      return arena.width - point.x;
    case 'left':
      return point.x;
    case 'bottom':
      return arena.height - point.y;
    case 'top':
      return point.y;
  }
}

export function pathWalked(state: SimState): number | null {
  const side = state.festival?.phase === 'path' ? state.festival.taken?.side : undefined;
  const walkers = state.players.filter((player) => player.gone !== true);
  if (side === undefined || walkers.length === 0) {
    return null;
  }
  const length = side === 'left' || side === 'right' ? state.arena.width : state.arena.height;
  const span = length - FESTIVAL.exit.depth - FESTIVAL.path.endDepth;
  const walked = walkers.map((player) =>
    clamp01((length - FESTIVAL.exit.depth - toEnd(player, state.arena, side)) / span),
  );
  return walked.reduce((sum, share) => sum + share, 0) / walked.length;
}

function arrive(ctx: StepContext, festival: FestivalState, exit: FestivalExit): void {
  const { state, content } = ctx;
  const stage = lookup(content.sets, exit.setId, 'set');
  const forward = FORWARD[exit.side];
  const centre = { x: state.arena.width / 2, y: state.arena.height / 2 };
  const entry = entrySpot(stage.arena, stage.id, OPPOSITE[exit.side]);
  const spots = state.players.map((player) =>
    across(entry, forward, offsetIn(player, centre, forward), stage.arena, player.radius),
  );
  state.events.push({ type: 'festivalArrived', setId: stage.id });
  const ids = (festival.challenges ?? [])
    .filter((board) => board.takenTick !== undefined)
    .map((board) => board.id);
  festival.scenes.push({
    setId: stage.id,
    fromTick: state.tick,
    ...(ids.length === 0
      ? {}
      : { challenges: { ids, scoreMul: challengeScoreMul(ids), scoreFrom: stageGains(state) } }),
  });
  delete festival.challenges;
  const scale = stageScale(festival.scenes.length - 1, ids);
  if (scale !== undefined) {
    state.enemyScale = scale;
  }
  if (stagesLeft(festival).length === 0) {
    state.trapDraw = FESTIVAL.lastStageTraps;
  }
  delete festival.taken;
  festival.phase = 'scene';
  enterSet(ctx, stage, spots);
}

const FORWARD: Readonly<Record<EdgeSide, Vec2>> = {
  right: { x: 1, y: 0 },
  left: { x: -1, y: 0 },
  bottom: { x: 0, y: 1 },
  top: { x: 0, y: -1 },
};

function startOf(arena: { width: number; height: number }, forward: Vec2): Vec2 {
  const { depth } = FESTIVAL.exit;
  return {
    x: forward.x === 0 ? arena.width / 2 : forward.x > 0 ? depth : arena.width - depth,
    y: forward.y === 0 ? arena.height / 2 : forward.y > 0 ? depth : arena.height - depth,
  };
}

function offsetIn(player: PlayerState, centre: Vec2, forward: Vec2): number {
  const offset = forward.x === 0 ? player.x - centre.x : player.y - centre.y;
  return clamp(offset, -FESTIVAL.exit.radius, FESTIVAL.exit.radius);
}

function across(
  from: Vec2,
  forward: Vec2,
  offset: number,
  arena: { width: number; height: number },
  radius: number,
): Vec2 {
  const x = forward.x === 0 ? from.x + offset : from.x;
  const y = forward.x === 0 ? from.y : from.y + offset;
  return {
    x: clamp(x, radius, arena.width - radius),
    y: clamp(y, radius, arena.height - radius),
  };
}

// The boards a player has never switched, close enough that the assist aims at them: it never lights
// again a pastille the player put out.
export function challengeTargets(
  state: SimState,
  playerId: PlayerId,
): { x: number; y: number; radius: number; hp: number }[] {
  const slot = state.players.findIndex((player) => player.id === playerId);
  const player = state.players[slot];
  const boards = state.festival?.phase === 'path' ? (state.festival.challenges ?? []) : [];
  if (player === undefined) {
    return [];
  }
  const near = FESTIVAL.challenges.assistReach;
  return boards
    .filter((board) => {
      const dx = board.x - player.x;
      const dy = board.y - player.y;
      return (
        board.takenTick === undefined &&
        board.switched[slot] !== true &&
        dx * dx + dy * dy <= (near + board.radius) * (near + board.radius)
      );
    })
    .map(({ x, y, radius }) => ({ x, y, radius, hp: 1 }));
}
